import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useLoader } from '../../../../../../helpers/useLoader';
import { InputStatus } from '../../../../../../models/error.model';
import { SaveFilters } from '../../components/filters/SaveFilters';
import { ISavedFilter } from '../../imageFilters.model';

const FadeOutDelay = 1500;

interface IProps {
  savedFilters: ISavedFilter[];
  onSavedFilterLoad(savedFilter: ISavedFilter): void;
  onSaveFilterAsync(name: string): Promise<void>;
  onFilterDeleteAsync(id: string): Promise<void>;
}

const SaveFiltersContainer = observer((props: IProps): ReactElement => {
  const { isLoading, withLoaderAsync } = useLoader();

  const [isNewFilterNameUsed, setIsNewFilterNameUsed] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [newFilterName, setNewFilterName] = useState<string>('');
  const [newFilterNameStatus, setNewFilterNameStatus] = useState<InputStatus>(InputStatus.empty());
  const [selectedFilter, setSelectedFilter] = useState<ISavedFilter | undefined>(undefined);
  const [showFilterLoadedMessage, setShowFilterLoadedMessage] = useState<boolean>(false);
  const [showFilterSavedMessage, setShowFilterSavedMessage] = useState<boolean>(false);

  const handleNewFilterNameChange = (name: string): void => {
    setNewFilterName(name);
    setNewFilterNameStatus(InputStatus.empty());
    setIsNewFilterNameUsed(false);
    setShowDeleteConfirmation(false);
  };

  const handleNewFilterNameBlur = (): void => {
    setNewFilterNameStatus(!newFilterName ? InputStatus.empty() : InputStatus.valid());
    setShowDeleteConfirmation(false);
    setIsNewFilterNameUsed(false);
  };

  const handleFilterSelected = (selectedFilter?: ISavedFilter): void => {
    setSelectedFilter(selectedFilter);
    setShowDeleteConfirmation(false);
    setNewFilterNameStatus(InputStatus.empty());
    setIsNewFilterNameUsed(false);
  };

  const handleSaveFilterAsync = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      if (props.savedFilters.some((x) => x.name === newFilterName)) {
        setIsNewFilterNameUsed(true);
        setNewFilterNameStatus(InputStatus.invalid());
      } else {
        await handleSaveConfirmedAsync();
      }
    });
  };

  const handleSaveConfirmedAsync = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      await props.onSaveFilterAsync(newFilterName);
      setIsNewFilterNameUsed(false);
      setNewFilterName('');
      setNewFilterNameStatus(InputStatus.empty());
      setShowFilterSavedMessage(true);

      setTimeout(() => {
        setShowFilterSavedMessage(false);
      }, FadeOutDelay);
    });
  };

  const handleFilterOverrideDeclined = (): void => {
    setIsNewFilterNameUsed(false);
    setNewFilterName('');
    setNewFilterNameStatus(InputStatus.empty());
  };

  const handleSelectedFilterDelete = (): void => {
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmedAsync = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      if (selectedFilter) {
        await props.onFilterDeleteAsync(selectedFilter.id);
        setShowDeleteConfirmation(false);
        setSelectedFilter(undefined);
      }
    });
  };

  const handleDeleteDeclined = (): void => {
    setShowDeleteConfirmation(false);
    setSelectedFilter(undefined);
  };

  const handleSelectedFilterLoad = (): void => {
    if (selectedFilter) {
      props.onSavedFilterLoad(selectedFilter);
      setSelectedFilter(undefined);
      setShowFilterLoadedMessage(true);

      setTimeout(() => {
        setShowFilterLoadedMessage(false);
      }, FadeOutDelay);
    }
  };

  return (
    <SaveFilters
      isLoading={isLoading}
      showDeleteConfirmation={showDeleteConfirmation}
      newFilterName={newFilterName}
      savedFilters={props.savedFilters}
      selectedFilter={selectedFilter}
      isNewFilterNameUsed={isNewFilterNameUsed}
      newFilterNameStatus={newFilterNameStatus}
      showFilterLoadedMessage={showFilterLoadedMessage}
      showFilterSavedMessage={showFilterSavedMessage}
      onNewFilterNameChange={handleNewFilterNameChange}
      onNewFilterNameBlur={handleNewFilterNameBlur}
      onFilterSave={handleSaveFilterAsync}
      onFilterSelected={handleFilterSelected}
      onSelectedFilterDelete={handleSelectedFilterDelete}
      onSelectedFilterLoad={handleSelectedFilterLoad}
      onFilterOverrideConfirmed={handleSaveConfirmedAsync}
      onFilterOverrideDeclined={handleFilterOverrideDeclined}
      onFilterDeleteConfirmed={handleDeleteConfirmedAsync}
      onFilterDeleteDeclined={handleDeleteDeclined}
    />
  );
});

export { SaveFiltersContainer };
